<template>
    <div class="app-questionnaire">
        <app-question
            v-for="(question, index) in orderedQuestions"
            :key="index"
            :ref="`question-${question.id}`"
            :question="question"
            :disabled="disabled"
            :hide-checkbox="hideCheckbox"
            :show-error="showError || forceShowError"
            @answer:selected="$emit('change', { results, is_valid, is_complete })"
        >
            <template v-if="questionSlots.includes(`question.${question.key}`)" #answer="data">
                <slot :name="`question.${question.key}`" v-bind="data" />
            </template>
        </app-question>
        <v-layout v-if="!hideActions" class="app-form__actions primaryLight mt-4 py-4 px-5 rounded justify-end">
            <template v-if="findSlot('actions')">
                <slot name="actions" v-bind="{ errors, loading, valid, form, onSubmit }" />
            </template>
            <template v-else>
                <template v-if="findSlot('prepend-actions')">
                    <span class="mr-2">
                        <slot name="prepend-actions" v-bind="{ errors, loading, valid, form, onSubmit }" />
                    </span>
                </template>

                <app-button
                    :loading="loading"
                    :title="$t('submit')"
                    :text="submitButtonText || $t('submit')"
                    :icon="submitButtonIcon"
                    @click="onSubmit"
                />
            </template>
        </v-layout>
    </div>
</template>

<script>
import { AppQuestion } from '@/models';
import scrollToElement from '@/lib/helpers/scrollToElement';

export default {
    name: 'AppQuestionnaire',
    props: {
        value: {
            type: Array,
            default: () => [],
            validator: items => {
                if (!Array.isArray(items)) return false;
                return items.every(item => item instanceof AppQuestion);
            }
        },
        disabled: {
            type: Boolean,
            default: false
        },
        hideCheckbox: {
            type: Boolean,
            default: false
        },
        hideActions: {
            type: Boolean,
            default: false
        },
        submitButtonText: {
            type: String,
            default: null
        },
        submitButtonIcon: {
            type: String,
            default: 'submit'
        },
        returnQuestions: {
            type: Boolean,
            default: false
        },
        showCorrectAnswer: {
            type: Boolean,
            default: false
        },
        forceShowError: {
            type: Boolean,
            default: false
        }
    },
    data: () => ({
        showError: false,
        loading: false
    }),
    computed: {
        questions() {
            return this.value.filter(question => !question.is_type_section);
        },
        sections() {
            return this.value.filter(question => question.is_type_section);
        },
        orderedQuestions() {
            const sections = this.sections;
            sections.sort((a, b) => a.sort_order - b.sort_order);

            const questions = this.questions;
            questions.sort((a, b) => a.sort_order - b.sort_order);

            if (!sections.length) {
                return questions;
            }

            const ordered = sections.reduce((acc, section) => {
                const sectionQuestions = questions.filter(question => question.section === section.key);

                // Don't display sections that have no questions
                // if (!sectionQuestions.length) {
                //     return acc;
                // }

                acc.push(section);
                acc.push(...sectionQuestions);

                return acc;
            }, []);

            // Display questions without sections at the end of the questionnaire
            ordered.push(...this.standaloneQuestions);

            return ordered;
        },
        standaloneQuestions() {
            return this.questions.filter(question => !question.section);
        },
        results() {
            return this.questions.map(question => question.result);
        },
        is_valid() {
            return this.questions.every(question => question.is_valid);
        },
        is_complete() {
            return this.questions.every(question => question.is_complete);
        },
        questionSlots() {
            return this._.keys(
                this._.pickBy(this.$scopedSlots, (v, k) => {
                    return k.startsWith('question.');
                })
            );
        }
    },
    watch: {
        value: {
            deep: true,
            immediate: true,
            handler(newVal, oldVal) {
                if (newVal !== oldVal) {
                    this.$emit('input', this.inputValue);
                }
            }
        }
    },
    async mounted() {
        await this.$nextTick();
        this.$emit('load', this.value);
    },
    methods: {
        async onSubmit() {
            this.loading = true;

            if (!this.is_complete) {
                this.showError = true;
                await this.$nextTick();
                scrollToElement('.app-question--error');
                this.loading = false;
                return false;
            }

            // if (this.showCorrectAnswer && !this.is_valid) {
            //     this.showError = true;
            //     await this.$nextTick();
            //     scrollToElement('.app-question--error');
            //     this.loading = false;
            //     return false;
            // }

            this.loading = false;

            if (this.returnQuestions) {
                this.$emit('questionnaire:submit', this.value);
                return this.value;
            } else {
                this.$emit('questionnaire:submit', this.results);
                return this.results;
            }
        }
    }
};
</script>

<style lang="scss" scoped>
.app-questionnaire {
    display: block;
}
</style>
